import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";
import { walletAPI } from "../services/api";
import { filterAndNormalizeTransactions } from "../utils/transactionFilters";

const Transactions = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rawTransactions, setRawTransactions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("All Time");
  const [loading, setLoading] = useState(false);

  const typeOptions = ["All", "Deposit", "Withdrawal", "Arbitrage Trade", "Gold Trade", "Bonus"];
  const statusOptions = ["All", "Completed", "Pending", "Failed"];
  const dateOptions = ["All Time", "Last 7 Days", "Last 30 Days", "Last 90 Days"];

  const filterPayload = useMemo(
    () => ({ typeFilter, statusFilter, dateFilter, searchTerm }),
    [typeFilter, statusFilter, dateFilter, searchTerm],
  );

  const filteredAll = useMemo(
    () => filterAndNormalizeTransactions(rawTransactions, filterPayload),
    [rawTransactions, filterPayload],
  );

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const res = await walletAPI.getTransactions("page=1&limit=1000");
        const allTransactions = Array.isArray(res?.transactions)
          ? res.transactions
          : [];
        setRawTransactions(allTransactions);
      } catch (e) {
        setRawTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  useEffect(() => {
    const pageSize = 10;
    const totalFiltered = filteredAll.length;
    const safeTotalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
    const page = Math.min(currentPage, safeTotalPages);
    const startIdx = (page - 1) * pageSize;
    setTransactions(filteredAll.slice(startIdx, startIdx + pageSize));
    setTotalPages(safeTotalPages);
    if (page !== currentPage) setCurrentPage(page);
  }, [filteredAll, currentPage]);


  const getStatusClass = (color) => {
    switch (color) {
      case "gold":
        return "text-primary";
      case "yellow":
        return "text-yellow-400";
      case "red":
        return "text-red-400";
      default:
        return "text-[var(--text-primary)]";
    }
  };

  const handleOpenReports = () => {
    const params = new URLSearchParams();
    params.set("reportType", "Transaction Summary");
    if (typeFilter && typeFilter !== "All") params.set("txType", typeFilter);
    if (statusFilter && statusFilter !== "All") params.set("txStatus", statusFilter);
    if (dateFilter && dateFilter !== "All Time") params.set("txDatePreset", dateFilter);
    if (searchTerm.trim()) params.set("txSearch", searchTerm.trim());
    navigate(`/reports?${params.toString()}`);
  };

  const handleView = (txId) => {
    console.log("Viewing transaction:", txId);
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)] overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 min-w-0 p-4 md:p-8 lg:ml-64 overflow-x-hidden">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Recent Transactions
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            View and manage all your transaction history
          </p>
        </div>

        {/* Filter & Action Bar */}
        <Card className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Search Input */}
            <div className="md:col-span-5">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search transactions…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary focus:shadow-lg focus:shadow-[#22c55e]/20 transition-all placeholder-[#6B7280]"
                />
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-primary)] opacity-70"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Filter Dropdowns */}
            <div className="md:col-span-5 flex flex-wrap gap-2">
              {/* Transaction Type Filter */}
              <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }} className="w-full sm:w-auto min-w-0 px-4 py-3 rounded-xl border-2 border-primary text-[var(--text-primary)] bg-[var(--bg-main)] focus:outline-none">
                {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {/* Status Filter */}
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="w-full sm:w-auto min-w-0 px-4 py-3 rounded-xl border-2 border-primary text-[var(--text-primary)] bg-[var(--bg-main)] focus:outline-none">
                {statusOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {/* Date Range Filter */}
              <select value={dateFilter} onChange={e => { setDateFilter(e.target.value); setCurrentPage(1); }} className="w-full sm:w-auto min-w-0 px-4 py-3 rounded-xl border-2 border-primary text-[var(--text-primary)] bg-[var(--bg-main)] focus:outline-none">
                {dateOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="md:col-span-2 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleOpenReports}
                className="w-full px-4 py-3 rounded-xl border-2 border-primary text-primary font-bold hover:bg-[#22c55e]/10 transition-all flex items-center justify-center gap-2"
              >
                Report
              </button>
            </div>
          </div>
        </Card>

        {/* Transactions Table Card */}
        <Card>
          <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Transaction History
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <span className="ml-4 text-[var(--text-primary)]">Loading transactions...</span>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--text-primary)] text-lg">No transactions found</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-primary/20">
                      <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                        Transaction ID
                      </th>
                      <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                        Type
                      </th>
                      <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                        Amount
                      </th>
                      <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                        Status
                      </th>
                      <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                        Date & Time
                      </th>
                      <th className="text-center py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, index) => (
                      <tr
                        key={tx.id}
                        className={`border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 transition-colors ${
                          index % 2 === 1 ? "bg-surface-softer" : ""
                        }`}
                      >
                        <td className="py-4 px-4">
                          <span className="font-mono text-sm text-[var(--text-primary)]">
                            {tx.id}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-[var(--text-primary)] font-semibold">
                          {tx.displayType}
                        </td>
                        <td className="py-4 px-4 text-[var(--text-primary)]">{tx.amount}</td>
                        <td className="py-4 px-4">
                          <span
                            className={`text-sm font-semibold ${getStatusClass(
                              tx.statusColor
                            )}`}
                          >
                            {tx.status}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-[var(--text-primary)] text-sm">
                          {tx.date} {tx.time}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <button
                            onClick={() => handleView(tx.id)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/30 text-[var(--text-primary)] hover:bg-[#22c55e]/10 hover:border-primary transition-all"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {transactions.map((tx) => (
                  <div
                    key={tx.id}
                    className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-mono text-sm text-[var(--text-primary)] mb-1">{tx.id}</p>
                        <p className="text-xs text-[var(--text-primary)] opacity-70">
                          {tx.date} {tx.time}
                        </p>
                      </div>
                      <span
                        className={`text-sm font-semibold ${getStatusClass(
                          tx.statusColor
                        )}`}
                      >
                        {tx.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">Type</p>
                        <p className="text-sm font-semibold text-[var(--text-primary)]">{tx.displayType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-primary)] opacity-70 mb-1">Amount</p>
                        <p className="text-sm text-[var(--text-primary)]">{tx.amount}</p>
                      </div>
                    </div>

                    <button
                      onClick={() => handleView(tx.id)}
                      className="w-full px-4 py-2 rounded-lg border border-primary/30 text-[var(--text-primary)] hover:bg-[#22c55e]/10 hover:border-primary transition-all flex items-center justify-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      View Details
                    </button>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg border border-primary/30 text-[var(--text-primary)] hover:bg-[#22c55e]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      currentPage === page
                        ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white"
                        : "border border-primary/30 text-[var(--text-primary)] hover:bg-[#22c55e]/10"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg border border-primary/30 text-[var(--text-primary)] hover:bg-[#22c55e]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </>
          )}
        </Card>

        {/* Footer */}
        <div className="text-center py-8 mt-8">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
